import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { EmptyChart } from './EmptyChart'
import { usePromotionDecisionRate } from '../hooks/usePromotionDecisionRate'
import type { LegendProps } from 'recharts'
import { PopulationSheet } from './PopulationSheet'
import { useState } from 'react'

// Custom legend for smaller, default color, aligned labels
function CustomLegend({ payload }: LegendProps) {
  if (!payload) return null;
  return (
    <div className="flex gap-6 mt-4 justify-center">
      {payload.map((entry) => (
        <div key={entry.value} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export function PromotionDecisionRateChart({ className }: { className?: string }) {
  const { data, isLoading, error } = usePromotionDecisionRate()
  const [sheetOpen, setSheetOpen] = useState(false)
  // TODO: Replace with real role check
  const canEdit = true

  if (isLoading) return <ChartSkeleton className={className} />
  if (error) return <ChartError className={className} />
  if (!data?.length) return <EmptyChart message="No promotion decision rate data available" className={className} />

  const chartConfig = {
    promote: {
      label: 'Promote',
      color: 'hsl(var(--chart-1))',
    },
    defer: {
      label: 'Defer',
      color: 'hsl(var(--chart-2))',
    },
  }

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Promotion Decision Rate by Year</h3>
        <p className="text-base text-muted-foreground">Promote vs Defer decisions last 5 years</p>
      </div>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="year" className="text-xs" />
            <YAxis className="text-xs" />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-md">
                      <div className="text-xs font-semibold mb-1">{label}</div>
                      <div className="space-y-1">
                        {payload.map((entry, index) => (
                          <div key={index} className="flex items-center justify-between w-full gap-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="h-2 w-2 rounded-sm" 
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-xs">
                                {entry.name}
                              </span>
                            </div>
                            <span className="text-xs font-semibold">
                              {entry.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend content={<CustomLegend />} />
            <Bar 
              dataKey="promote" 
              name={chartConfig.promote.label} 
              fill={chartConfig.promote.color} 
              barSize={40}
            />
            <Bar 
              dataKey="defer" 
              name={chartConfig.defer.label} 
              fill={chartConfig.defer.color} 
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {canEdit && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            className="text-primary underline text-sm hover:text-primary/80"
            onClick={() => setSheetOpen(true)}
          >
            Edit aggregates
          </button>
        </div>
      )}
      <PopulationSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  )
} 